import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Plus, Send, Trash2, MessageCircle, ChevronLeft, X, Share2, Lock, Zap, Sparkles } from 'lucide-react';
import OperatorMiraPanel from '@/components/OperatorMiraPanel';
import OperatorMiraDoctrineTile from '@/components/OperatorMiraDoctrineTile';
import MiraCheatGuide from '@/components/MiraCheatGuide';

function groupByWeek(convos) {
  const now = new Date();
  const weekAgo = new Date(now - 7 * 86400000);
  const twoWeeksAgo = new Date(now - 14 * 86400000);
  const groups = { 'This Week': [], 'Last Week': [], 'Earlier': [] };
  convos.forEach(c => {
    const d = new Date(c.updated_at || c.created_at);
    if (d >= weekAgo) groups['This Week'].push(c);
    else if (d >= twoWeeksAgo) groups['Last Week'].push(c);
    else groups['Earlier'].push(c);
  });
  return groups;
}

/** Lightweight markdown: **bold**, *italic*, - bullet lists */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="list-disc list-inside my-1 space-y-0.5">{listItems}</ul>);
      listItems = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(<li key={idx} className="text-sm">{inlineFormat(trimmed.slice(2))}</li>);
    } else {
      flushList();
      if (trimmed === '') {
        elements.push(<br key={idx} />);
      } else {
        elements.push(<p key={idx} className="text-sm leading-relaxed mb-1">{inlineFormat(trimmed)}</p>);
      }
    }
  });
  flushList();
  return elements;
}

function inlineFormat(text) {
  // Bold **text** and italic *text*
  const parts = [];
  let remaining = text;
  let key = 0;
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIdx = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) parts.push(<span key={key++}>{text.slice(lastIdx, match.index)}</span>);
    if (match[2]) parts.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={key++}>{match[3]}</em>);
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) parts.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return parts.length > 0 ? parts : text;
}

function ThinkingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: '#2A7B88' }}>M</div>
      <div className="flex gap-1 ml-2">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#2A7B88', animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
      <span className="text-xs ml-2 italic" style={{ color: '#9CA3AF' }}>Mira is thinking...</span>
    </div>
  );
}

export default function MiraChat() {
  const { user } = useAuth();
  const canOperate = user && (user.role === 'ceo' || user.role === 'leader');
  const initialTab = (() => {
    try { return localStorage.getItem('mira.tab') || 'ask'; } catch { return 'ask'; }
  })();
  const [tab, setTab] = useState(canOperate ? initialTab : 'ask');
  const switchTab = (t) => {
    setTab(t);
    try { localStorage.setItem('mira.tab', t); } catch {}
  };

  if (!canOperate) {
    // Sub-leaders, submitters, cost-controllers, viewers: advisor only
    return <MiraChatAdvisor />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4.5rem)] -my-6 -mx-4 sm:-mx-6" data-testid="mira-unified-page">
      <div
        className="flex items-center gap-1 px-4 py-2 border-b bg-white"
        style={{ borderColor: '#E5E1DC' }}
      >
        <button
          onClick={() => switchTab('ask')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${tab === 'ask' ? 'text-white' : ''}`}
          style={tab === 'ask' ? { backgroundColor: '#1B2A4A' } : { color: '#6B645B' }}
          data-testid="mira-tab-ask"
        >
          <Sparkles size={14} /> Ask Mira
        </button>
        <button
          onClick={() => switchTab('operator')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${tab === 'operator' ? 'text-white' : ''}`}
          style={tab === 'operator' ? { backgroundColor: '#2A7B88' } : { color: '#6B645B' }}
          data-testid="mira-tab-operator"
        >
          <Zap size={14} /> Operator Mira
        </button>
        <span
          className="ml-auto text-[11px] px-2 py-0.5 rounded"
          style={{ backgroundColor: '#F7F5F2', color: '#6B645B' }}
        >
          {tab === 'ask' ? 'Advisor · read-only' : 'Command · writes to your division, previewed & undoable'}
        </span>
      </div>

      <div className="flex-1 overflow-hidden">
        {tab === 'ask' ? (
          <MiraChatAdvisor />
        ) : (
          <div className="h-full overflow-y-auto p-4 sm:p-6 space-y-4 bg-white">
            <MiraCheatGuide />
            <OperatorMiraDoctrineTile />
            <div className="rounded-xl border bg-white" style={{ borderColor: '#E5E1DC', height: 560 }}>
              <OperatorMiraPanel />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiraChatAdvisor() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.get('/mira/conversations').then(r => {
      setConversations(r.data);
      if (r.data.length > 0) loadConversation(r.data[0].id);
      else createNew();
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const loadConversation = async (id) => {
    const r = await api.get(`/mira/conversations/${id}`);
    setActiveConvo(r.data);
    setMessages(r.data.messages || []);
    setSidebarOpen(false);
  };

  const createNew = async () => {
    const r = await api.post('/mira/conversations');
    setConversations(prev => [r.data, ...prev]);
    setActiveConvo(r.data);
    setMessages(r.data.messages || []);
    setSidebarOpen(false);
  };

  const deleteConvo = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    await api.delete(`/mira/conversations/${id}`);
    setConversations(prev => prev.filter(c => c.id !== id));
    if (activeConvo?.id === id) {
      setActiveConvo(null);
      setMessages([]);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending || !activeConvo) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic: add user message immediately
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const r = await api.post(`/mira/conversations/${activeConvo.id}/message`, { message: text });
      setMessages(prev => [...prev.slice(0, -1), r.data.user_message, r.data.assistant_message]);
      // Update conversation title in sidebar
      setConversations(prev => prev.map(c =>
        c.id === activeConvo.id ? { ...c, title: text.slice(0, 50), updated_at: new Date().toISOString() } : c
      ));
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const grouped = groupByWeek(conversations);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4.5rem)] -my-6 -mx-4 sm:-mx-6" data-testid="mira-chat">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'flex' : 'hidden'} sm:flex flex-col w-full sm:w-72 border-r bg-white flex-shrink-0`} style={{ borderColor: '#E5E1DC' }}>
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
          <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Conversations</h3>
          <button onClick={createNew} className="p-1.5 rounded-md text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="new-convo-btn">
            <Plus size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-3">
          {Object.entries(grouped).map(([label, convos]) => convos.length > 0 && (
            <div key={label}>
              <p className="text-[10px] font-bold uppercase tracking-wider px-2 mb-1" style={{ color: '#9CA3AF' }}>{label}</p>
              {convos.map(c => (
                <button key={c.id} onClick={() => loadConversation(c.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between group ${activeConvo?.id === c.id ? 'bg-[#EBF5F7]' : 'hover:bg-gray-50'}`}
                  data-testid={`convo-${c.id}`}>
                  <span className="truncate" style={{ color: '#1B2A4A' }}>{c.title}</span>
                  <button onClick={(e) => deleteConvo(c.id, e)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200">
                    <Trash2 size={12} style={{ color: '#D4726A' }} />
                  </button>
                </button>
              ))}
            </div>
          ))}
          {conversations.length === 0 && (
            <p className="text-xs text-center py-6" style={{ color: '#9CA3AF' }}>No conversations yet</p>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-white min-w-0">
        {/* Mobile sidebar toggle */}
        <div className="sm:hidden p-2 border-b flex items-center gap-2" style={{ borderColor: '#E5E1DC' }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            {sidebarOpen ? <X size={16} /> : <MessageCircle size={16} />}
          </button>
          <span className="text-sm font-medium truncate" style={{ color: '#1B2A4A' }}>{activeConvo?.title || 'Talk to Mira'}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-4" style={{ backgroundColor: '#FAFAF8' }}>
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${idx}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 mt-1 mr-2"
                  style={{ backgroundColor: '#2A7B88' }}>M</div>
              )}
              <div className={`max-w-[80%] sm:max-w-[65%] rounded-2xl px-4 py-3 ${
                msg.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'
              }`} style={{
                backgroundColor: msg.role === 'user' ? '#E0F2F4' : '#F7F5F2',
                border: msg.role === 'assistant' ? '1px solid #E5E1DC' : 'none',
                fontFamily: msg.role === 'assistant' ? 'Georgia, "Times New Roman", serif' : 'inherit',
                color: '#1B2A4A',
              }}>
                {msg.role === 'assistant' ? renderMarkdown(msg.content) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                )}
                <p className="text-[10px] mt-1.5" style={{ color: '#9CA3AF' }}>
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            </div>
          ))}
          {sending && <ThinkingDots />}
          <div ref={messagesEndRef} />
        </div>

        {/* Share toggle + Input */}
        <div className="border-t" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
          {activeConvo && (
            <div className="flex items-center justify-end px-4 pt-2">
              <button
                onClick={async () => {
                  const newVal = !activeConvo.shared_with_ceo;
                  await api.post(`/mira/conversations/${activeConvo.id}/share`, { shared: newVal }).catch(() => {});
                  setActiveConvo(prev => ({ ...prev, shared_with_ceo: newVal }));
                  setConversations(prev => prev.map(c => c.id === activeConvo.id ? { ...c, shared_with_ceo: newVal } : c));
                }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md hover:bg-gray-50 transition-colors"
                style={{ color: activeConvo.shared_with_ceo ? '#2A7B88' : '#9CA3AF' }}
                data-testid="share-toggle"
              >
                {activeConvo.shared_with_ceo ? <Share2 size={12} /> : <Lock size={12} />}
                {activeConvo.shared_with_ceo ? 'Shared with Dipali' : 'Private conversation'}
              </button>
            </div>
          )}
          <div className="p-3 sm:p-4 pt-2">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask Mira anything about your division..."
                rows={1}
                className="flex-1 px-4 py-2.5 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2A7B88]"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A', maxHeight: '120px' }}
                data-testid="mira-input"
              />
              <button onClick={handleSend} disabled={sending || !input.trim()}
                className="p-2.5 rounded-xl text-white disabled:opacity-40 transition-opacity flex-shrink-0"
                style={{ backgroundColor: '#1B2A4A' }}
                data-testid="mira-send-btn">
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
